// Observes an ad slot element via IntersectionObserver. When the element enters
// the viewport (plus a MARGIN rootMargin buffer for pre-loading), it looks up
// its zone config from window.adConfig.adZones and fires window.addAdZone.
//
// Usage in views:
//   data-controller="ad-slot"
//   data-ad-slot-code-value="ad-slot-results-page-inline"
//
// The code (data-ad-slot-code-value) is passed directly from Gam::AdZone#code
// so the controller never needs to derive or guess it.
import { Controller } from "stimulus"

export default class extends Controller {
  // Buffer in px used for both the synchronous viewport check and the
  // IntersectionObserver rootMargin — keeps the two detection strategies aligned.
  static MARGIN = 800

  static values = {
    code: String
  }

  connect() {
    if (!this.codeValue || !window.adConfig) return

    // Synchronously check if the element is already in view before setting up
    // the observer. This avoids race conditions where the element is rendered
    // but the observer hasn't computed its first entry yet.
    const margin = this.constructor.MARGIN
    const rect = this.element.getBoundingClientRect()
    const inView = rect.top < (window.innerHeight + margin) && rect.bottom > -margin

    if (inView) {
      this.loadSlot()
      return
    }

    this.observed = false
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !this.observed) {
          this.observed = true
          this.loadSlot()
          this.observer.unobserve(this.element)
        }
      }
    }, { rootMargin: `${margin}px` })

    this.observer.observe(this.element)
  }

  loadSlot() {
    if (!window.addAdZone) return
    
    const slotConfig = window.adConfig.adZones.find((s) => s.code === this.codeValue)
    if (!slotConfig) return

    window.addAdZone(slotConfig)
  }

  disconnect() {
    this.observer?.disconnect()
  }
}